import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { PlayerDataContext, SocketContext } from '../../../App';

import mapImage from '../../../images/map.jpg';
import relativeMapImage from '../../../images/map-relative.png';
import positionMapCoords from '../positions';
import pawnImages from '../../../constants/pawnImages';
import canPawnMove from './canPawnMove';
import getPositionAfterMove from './getPositionAfterMove';
import { getTwoPlayerVisibleKey } from '../../../utils/twoPlayerColors';

// Build the step-by-step intermediate positions a pawn walks through.
// Each color has its own wrap/entry logic so we trace each +1 step manually.
const stepOnePos = (color, pos) => {
    // base → starting square
    const BASE = { red: [0,1,2,3], blue: [4,5,6,7], green: [8,9,10,11], yellow: [12,13,14,15] };
    const START = { red: 16, blue: 55, green: 42, yellow: 29 };
    if (BASE[color]?.includes(pos)) return START[color];

    // home-column entries (board wraps at 67 into color home column)
    // These offsets match getPositionAfterMove logic
    const HOME_OFFSETS = { blue: -52, green: -52, yellow: -52 };
    const HOME_ENTER   = { red: 67, blue: 67, green: 67, yellow: 67 };
    const HOME_COL_GAP = { blue: 20, green: 39, yellow: 58 }; // skip gap going into home col

    if (color !== 'red' && pos === HOME_ENTER[color]) {
        // wrapping from 67 to home-column start
        return pos + 1 + (HOME_OFFSETS[color] ?? 0);
    }
    // home-column gap skip
    if (color === 'blue'   && pos === 53) return pos + 1 + HOME_COL_GAP.blue;
    if (color === 'green'  && pos === 40) return pos + 1 + HOME_COL_GAP.green;
    if (color === 'yellow' && pos === 27) return pos + 1 + HOME_COL_GAP.yellow;
    // red home-column gap: positions 67 → go to 68 is straight +1
    if (color === 'red' && pos === 66) return pos + 2; // 66→68

    return pos + 1;
};

const getWalkPath = (color, fromPos, toPos) => {
    if (fromPos === toPos) return [];
    const path = [];
    let cur = fromPos;
    const MAX = 92;
    const seenPositions = new Set([fromPos]);
    for (let i = 0; i < MAX; i++) {
        cur = stepOnePos(color, cur);
        path.push(cur);
        if (cur === toPos) break;
        if (seenPositions.has(cur)) break; // Prevent infinite loops
        seenPositions.add(cur);
    }
    return path;
};

const STEP_MS = 65; // ms per board step
const BOARD_SIZE = 460;

// Ludo King-style 2-player perspective: every device sees its own tokens as
// blue in the bottom-left home and the opponent as green in the top-right.
// The server keeps authoritative logical colors/positions; only canvas drawing
// is transformed for the local viewer.
const ROTATION_TURNS_TO_BLUE_HOME = {
    blue: 0,
    green: 1,
    yellow: 2,
    red: 3,
};

const rotateCoords = (coords, quarterTurns) => {
    if (!coords || !quarterTurns) return coords;
    const turns = ((quarterTurns % 4) + 4) % 4;
    if (turns === 1) return { x: BOARD_SIZE - coords.y, y: coords.x };
    if (turns === 2) return { x: BOARD_SIZE - coords.x, y: BOARD_SIZE - coords.y };
    if (turns === 3) return { x: coords.y, y: BOARD_SIZE - coords.x };
    return coords;
};
const VISIBLE_STAR_POSITIONS = Object.freeze([24, 37, 50, 63]);

export const clientPointToCanvasPoint = (canvas, clientX, clientY) => {
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
};

const PAWN_GLOW = {
    red: '239, 68, 68',
    blue: '59, 130, 246',
    green: '34, 197, 94',
    yellow: '234, 179, 8',
};

const GameMap = ({ pawns, nowMoving, rolledNumber, moveSeqRef, openPawnValues = [1, 6], isTwoPlayerMode = false, viewerColor = null }) => {
    const player = useContext(PlayerDataContext);
    const socket = useContext(SocketContext);
    const canvasRef = useRef(null);
    const backgroundImageRef = useRef(null);
    const pawnImageCacheRef = useRef({});
    const [hintPawn, setHintPawn] = useState();
    const [selectedMovePreview, setSelectedMovePreview] = useState(null);

    // Animation state: override positions while walking
    const prevPawnsRef = useRef(null);           // positions before last update
    const animFrameRef = useRef(null);           // setTimeout id
    const [animOverride, setAnimOverride] = useState(null);
    // Optimistic move: we animate our own pawn immediately on click and hold it
    // at the target square until the server confirms (or the revert timer fires).
    const optimisticMoveRef = useRef(null);      // { pawnId, to }
    const revertTimerRef = useRef(null);
    const pawnHitAreasRef = useRef(new Map());
    const logicalViewerColor = viewerColor || player?.color || null;

    const clearOptimisticMove = useCallback((pawnId, requestSnapshot = false) => {
        if (pawnId && optimisticMoveRef.current?.pawnId !== pawnId) return;
        optimisticMoveRef.current = null;
        if (revertTimerRef.current) {
            clearTimeout(revertTimerRef.current);
            revertTimerRef.current = null;
        }
        if (animFrameRef.current) {
            clearTimeout(animFrameRef.current);
            animFrameRef.current = null;
        }
        setAnimOverride(null);
        setSelectedMovePreview(null);
        if (requestSnapshot && socket?.connected) socket.emit('room:data');
    }, [socket]);

    const relativeViewEnabled = Boolean(isTwoPlayerMode && logicalViewerColor);
    const displayRotationTurns = relativeViewEnabled
        ? (ROTATION_TURNS_TO_BLUE_HOME[logicalViewerColor] ?? 0)
        : 0;

    const getDisplayColor = useCallback((logicalColor) => {
        if (!relativeViewEnabled || logicalColor === 'grey') return logicalColor;
        return getTwoPlayerVisibleKey(logicalColor, logicalViewerColor);
    }, [relativeViewEnabled, logicalViewerColor]);

    const getDisplayCoords = useCallback((position) => {
        const coords = positionMapCoords[position];
        return rotateCoords(coords, displayRotationTurns);
    }, [displayRotationTurns]);

    const startWalkAnimation = useCallback((pawnId, steps) => {
        if (!steps.length) return;
        if (animFrameRef.current) clearTimeout(animFrameRef.current);
        let stepIdx = 0;
        const MAX_DURATION = 6000;
        const startTime = Date.now();

        const walk = () => {
            if (Date.now() - startTime > MAX_DURATION) {
                setAnimOverride(null);
                return;
            }
            setAnimOverride({ pawnId, steps, stepIdx });
            if (stepIdx < steps.length - 1) {
                stepIdx++;
                animFrameRef.current = setTimeout(walk, STEP_MS);
            } else {
                animFrameRef.current = setTimeout(() => {
                    // If this was an optimistic move the server hasn't confirmed yet,
                    // hold the pawn on the target square instead of snapping back.
                    if (optimisticMoveRef.current && optimisticMoveRef.current.pawnId === pawnId) {
                        setAnimOverride({ pawnId, steps, stepIdx: steps.length - 1, holding: true });
                    } else {
                        setAnimOverride(null);
                    }
                }, STEP_MS);
            }
        };
        walk();
    }, []);

    const paintPawn = useCallback((context, pawn) => {
        const coords = getDisplayCoords(pawn.position);
        if (!coords) return null;
        const { x, y } = coords;
        const displayColor = getDisplayColor(pawn.color);
        const touchableArea = new Path2D();
        touchableArea.arc(x, y, 12, 0, 2 * Math.PI);
        const image = pawnImageCacheRef.current[displayColor];
        context.save();
        context.beginPath();
        context.ellipse(x + 1, y + 12, 12, 5, 0, 0, 2 * Math.PI);
        context.fillStyle = 'rgba(0,0,0,0.28)';
        context.fill();
        if (image?.complete) {
            context.shadowColor = displayColor && PAWN_GLOW[displayColor] ? `rgba(${PAWN_GLOW[displayColor]}, 0.72)` : 'rgba(0,0,0,0.65)';
            context.shadowBlur = 12;
            context.shadowOffsetY = 4;
            context.drawImage(image, x - 17, y - 15, 35, 30);
        }
        context.restore();
        return touchableArea;
    }, [getDisplayColor, getDisplayCoords]);

    const getDisplayPawns = useCallback(() => {
        if (!animOverride) return pawns;
        return pawns.map(p => {
            if (p._id === animOverride.pawnId && animOverride.steps.length > 0) {
                const animPos = animOverride.steps[animOverride.stepIdx] ?? p.position;
                return { ...p, position: animPos };
            }
            return p;
        });
    }, [pawns, animOverride]);

    const rerenderCanvas = useCallback((overridePawns) => {
        const canvas = canvasRef.current;
        const backgroundImage = backgroundImageRef.current;
        if (!canvas || !backgroundImage?.complete) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

        // Draw visible stars only on the four standard white star-safe cells.
        // Coloured starting cells remain protected by server rules but should
        // not display a star symbol.
        VISIBLE_STAR_POSITIONS.forEach(position => {
            const coords = getDisplayCoords(position);
            if (!coords) return;
            const outerRadius = 9;
            const innerRadius = 4;
            ctx.save();
            ctx.beginPath();
            for (let point = 0; point < 10; point += 1) {
                const radius = point % 2 === 0 ? outerRadius : innerRadius;
                const angle = -Math.PI / 2 + (point * Math.PI / 5);
                const x = coords.x + Math.cos(angle) * radius;
                const y = coords.y + Math.sin(angle) * radius;
                if (point === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.fillStyle = 'rgba(255,255,255,0.96)';
            ctx.strokeStyle = 'rgba(15,23,42,0.72)';
            ctx.lineWidth = 1.4;
            ctx.shadowColor = 'rgba(0,0,0,0.28)';
            ctx.shadowBlur = 3;
            ctx.fill();
            ctx.stroke();
            ctx.restore();
        });

        if (animOverride?.steps?.length) {
            const movingPawn = pawns.find(p => p._id === animOverride.pawnId);
            const trailSteps = animOverride.steps.slice(animOverride.stepIdx);
            trailSteps.forEach((pos, idx) => {
                const coords = getDisplayCoords(pos);
                if (!coords) return;
                const displayColor = getDisplayColor(movingPawn?.color);
                const alpha = Math.max(0.14, 0.42 - idx * 0.06);
                ctx.beginPath();
                ctx.arc(coords.x, coords.y, 12 - Math.min(idx, 4), 0, Math.PI * 2);
                ctx.fillStyle = displayColor && PAWN_GLOW[displayColor]
                    ? `rgba(${PAWN_GLOW[displayColor]}, ${alpha})`
                    : `rgba(255,255,255,${alpha})`;
                ctx.shadowColor = displayColor || '#ffffff';
                ctx.shadowBlur = idx === 0 ? 22 : 12;
                ctx.fill();
                ctx.closePath();
            });
            ctx.shadowBlur = 0;
            ctx.shadowColor = 'transparent';
        }

        const displayPawns = overridePawns || getDisplayPawns();
        pawnHitAreasRef.current.clear();
        displayPawns.forEach(pawn => {
            const touchableArea = paintPawn(ctx, pawn);
            if (touchableArea) pawnHitAreasRef.current.set(pawn._id, touchableArea);
        });

        if (hintPawn) paintPawn(ctx, hintPawn);
        if (selectedMovePreview) {
            const previewCoords = getDisplayCoords(selectedMovePreview.position);
            if (previewCoords) {
                const previewColor = getDisplayColor(selectedMovePreview.color);
                ctx.save();
                ctx.beginPath();
                ctx.arc(previewCoords.x, previewCoords.y, 16, 0, Math.PI * 2);
                ctx.fillStyle = previewColor && PAWN_GLOW[previewColor]
                    ? `rgba(${PAWN_GLOW[previewColor]}, 0.28)`
                    : 'rgba(255,255,255,0.24)';
                ctx.shadowColor = previewColor || '#ffffff';
                ctx.shadowBlur = 18;
                ctx.fill();
                ctx.lineWidth = 2;
                ctx.strokeStyle = previewColor || '#ffffff';
                ctx.stroke();
                ctx.restore();
            }
        }
    }, [animOverride, getDisplayColor, getDisplayCoords, getDisplayPawns, hintPawn, paintPawn, pawns, selectedMovePreview]);

    // Detect pawn movement and start walk animation
    useEffect(() => {
        if (!prevPawnsRef.current) {
            prevPawnsRef.current = pawns.map(p => ({ _id: p._id, position: p.position, color: p.color }));
            return;
        }

        const prevById = new Map(prevPawnsRef.current.map(pp => [pp._id, pp]));
        prevPawnsRef.current = pawns.map(p => ({ _id: p._id, position: p.position, color: p.color }));

        let movedPawn = null;
        let fromPos = 0;
        for (const p of pawns) {
            const prev = prevById.get(p._id);
            if (!prev || prev.position === p.position) continue;

            // Server confirmed the move we already animated optimistically —
            // release the hold (or let the in-flight walk finish and clear itself).
            const optimistic = optimisticMoveRef.current;
            if (optimistic && p._id === optimistic.pawnId) {
                optimisticMoveRef.current = null;
                if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
                if (p.position === optimistic.to) {
                    setAnimOverride(cur => (cur && cur.pawnId === p._id && cur.holding ? null : cur));
                    continue;
                }
                // The server accepted a different authoritative position. Drop the
                // optimistic hold and animate to the server-provided square.
                if (animFrameRef.current) clearTimeout(animFrameRef.current);
                setAnimOverride(null);
            }

            // Pawn sent back to base (killed): snap instantly — the board path
            // never walks into base squares, so a step animation is meaningless.
            if (p.position <= 15 && prev.position > 15) continue;

            if (!movedPawn) {
                movedPawn = p;
                fromPos = prev.position;
            }
        }

        if (!movedPawn) return;

        const steps = getWalkPath(movedPawn.color, fromPos, movedPawn.position);
        startWalkAnimation(movedPawn._id, steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pawns]);

    // Re-render canvas whenever display state changes
    useEffect(() => {
        rerenderCanvas();
    }, [rerenderCanvas, animOverride]);

    const handleCanvasPointer = event => {
        if (!nowMoving || rolledNumber == null || animOverride || optimisticMoveRef.current) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const { x, y } = clientPointToCanvasPoint(canvas, event.clientX, event.clientY);
        for (const pawn of pawns) {
            const hitArea = pawnHitAreasRef.current.get(pawn._id);
            if (!hitArea || !ctx.isPointInPath(hitArea, x, y)) continue;
            if (pawn.color !== logicalViewerColor || !canPawnMove(pawn, rolledNumber, openPawnValues)) continue;

            const pawnPosition = getPositionAfterMove(pawn, rolledNumber, openPawnValues);
            if (pawnPosition == null || pawnPosition === pawn.position) break;

            // Start the local walk first. Socket.IO remains authoritative and the
            // next room:data snapshot confirms or corrects this visual state.
            setSelectedMovePreview({ pawnId: pawn._id, position: pawnPosition, color: pawn.color });
            optimisticMoveRef.current = { pawnId: pawn._id, from: pawn.position, to: pawnPosition };
            if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
            startWalkAnimation(pawn._id, getWalkPath(pawn.color, pawn.position, pawnPosition));
            revertTimerRef.current = setTimeout(() => {
                if (optimisticMoveRef.current?.pawnId === pawn._id) {
                    clearOptimisticMove(pawn._id, true);
                }
            }, 2600);

            const movePayload = {
                pawnId: pawn._id,
                seq: moveSeqRef ? moveSeqRef.current : undefined,
            };
            const handleMoveAck = (timeoutError, ack) => {
                if (timeoutError) {
                    // Request the source of truth immediately instead of leaving
                    // the player waiting on a long silent client timeout.
                    if (socket?.connected) socket.emit('room:data');
                    return;
                }
                if (ack?.ok === false) {
                    if (moveSeqRef && Number.isFinite(Number(ack.moveSeq))) {
                        moveSeqRef.current = Number(ack.moveSeq);
                    }
                    clearOptimisticMove(pawn._id, true);
                    return;
                }
                if (ack?.ok === true && moveSeqRef && Number.isFinite(Number(ack.moveSeq))) {
                    moveSeqRef.current = Number(ack.moveSeq);
                }
            };
            if (typeof socket?.timeout === 'function') {
                socket.timeout(1400).emit('game:move', movePayload, handleMoveAck);
            } else {
                socket.emit('game:move', movePayload);
            }
            break;
        }
        setHintPawn(null);
    };

    const handlePointerMove = event => {
        if (!nowMoving || !rolledNumber || event.pointerType === 'touch') return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const { x, y } = clientPointToCanvasPoint(canvas, event.clientX, event.clientY);
        canvas.style.cursor = 'default';
        for (const pawn of pawns) {
            const hitArea = pawnHitAreasRef.current.get(pawn._id);
            if (
                hitArea && ctx.isPointInPath(hitArea, x, y) &&
                logicalViewerColor === pawn.color &&
                canPawnMove(pawn, rolledNumber, openPawnValues)
            ) {
                const pawnPosition = getPositionAfterMove(pawn, rolledNumber, openPawnValues);
                if (pawnPosition != null && pawnPosition !== pawn.position) {
                    canvas.style.cursor = 'pointer';
                    if (hintPawn && hintPawn.id === pawn._id) return;
                    setHintPawn({ id: pawn._id, position: pawnPosition, color: 'grey' });
                    return;
                }
            }
        }
        setHintPawn(null);
    };

    useEffect(() => {
        if (!selectedMovePreview) return;
        const matchingPawn = pawns.find(pawn => pawn._id === selectedMovePreview.pawnId);
        if (!matchingPawn) {
            setSelectedMovePreview(null);
            return;
        }
        if (matchingPawn.position === selectedMovePreview.position || !nowMoving || rolledNumber == null) {
            setSelectedMovePreview(null);
        }
    }, [selectedMovePreview, pawns, nowMoving, rolledNumber]);

    useEffect(() => {
        const backgroundImage = new Image();
        backgroundImage.onload = () => rerenderCanvas();
        backgroundImageRef.current = backgroundImage;
        backgroundImage.src = isTwoPlayerMode ? relativeMapImage : mapImage;
        if (backgroundImage.complete) rerenderCanvas();
    // The source only changes when the room switches between normal and 2-player view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTwoPlayerMode]);

    useEffect(() => {
        Object.entries(pawnImages).forEach(([color, src]) => {
            const image = new Image();
            image.onload = () => rerenderCanvas();
            pawnImageCacheRef.current[color] = image;
            image.src = src;
            if (image.complete) rerenderCanvas();
        });
    // Pawn assets are static and only need loading once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => {
        if (animFrameRef.current) clearTimeout(animFrameRef.current);
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    }, []);

    return (
        <canvas
            className='canvas-container'
            width={460}
            height={460}
            ref={canvasRef}
            aria-label="Ludo board"
            data-viewer-color={logicalViewerColor || ''}
            data-move-preview={selectedMovePreview?.pawnId || ''}
            data-animation-step={animOverride?.stepIdx ?? ''}
            style={{ filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.32))', touchAction: 'manipulation' }}
            onPointerUp={handleCanvasPointer}
            onPointerMove={handlePointerMove}
        />
    );
};

// Only repaint the canvas when something that affects the board actually changes.
// Prevents full re-paints on unrelated parent re-renders (e.g. the 1s turn-countdown tick).
const arePropsEqual = (prev, next) => {
    if (prev.nowMoving !== next.nowMoving) return false;
    if (prev.rolledNumber !== next.rolledNumber) return false;
    if (prev.moveSeqRef !== next.moveSeqRef) return false;
    if (prev.isTwoPlayerMode !== next.isTwoPlayerMode) return false;
    if (prev.viewerColor !== next.viewerColor) return false;
    const a = prev.openPawnValues || [];
    const b = next.openPawnValues || [];
    if (a.length !== b.length || a.some((v, i) => v !== b[i])) return false;
    const pa = prev.pawns || [];
    const pb = next.pawns || [];
    if (pa.length !== pb.length) return false;
    for (let i = 0; i < pa.length; i++) {
        if (pa[i]?._id !== pb[i]?._id || pa[i]?.position !== pb[i]?.position || pa[i]?.color !== pb[i]?.color) return false;
    }
    return true;
};

export default React.memo(GameMap, arePropsEqual);
